import { createRoot } from 'react-dom/client'
import { createElement } from 'react'
import html2canvas from 'html2canvas'
import { jsPDF } from 'jspdf'
import { ClaimDetailView } from '@/components/claim/ClaimDetailView'
import { getMessage } from '@/utils/messages'
import vvsiLogo from '@/assets/vvslogo.png'
import type { ClaimGetResponse } from '@/types/claim'

const PDF_WIDTH_PT = 595.28 // A4 width in points
const PDF_HEIGHT_PT = 841.89 // A4 height in points
const MARGIN_PT = 24
const RENDER_WIDTH_PX = 794 // A4 @ 96dpi
const CANVAS_SCALE = 2

interface Block {
  top: number
  bottom: number
}

/** Top-level DOM children of the render container — each is one report section
 *  (a summary grid, an InfoCard/GridCard, etc.) that must not be split across pages. */
function getBlocks(container: HTMLElement): Block[] {
  return Array.from(container.children).map((el) => {
    const node = el as HTMLElement
    return {
      top: node.offsetTop * CANVAS_SCALE,
      bottom: (node.offsetTop + node.offsetHeight) * CANVAS_SCALE,
    }
  })
}

/**
 * Given the canvas-pixel offset a page starts at, returns where it must end —
 * a full page (`pageStart + maxSlicePx`) unless a block that starts within
 * this page would otherwise be split and would fit entirely on a fresh page,
 * in which case the page ends right before that block instead.
 *
 * Computed fresh per page (rather than a single precomputed break list) so it
 * always reflects the actual rendered `pageStart`, not an assumed one — a
 * mismatch between the two was producing pages with only a sliver of content
 * (visually blank) when a forced break landed just past a mid-block cut.
 */
function computeSliceEnd(blocks: Block[], pageStart: number, maxSlicePx: number, canvasHeight: number): number {
  let sliceEnd = Math.min(pageStart + maxSlicePx, canvasHeight)
  for (const block of blocks) {
    if (block.top >= pageStart && block.top < sliceEnd && block.bottom > sliceEnd) {
      const blockHeight = block.bottom - block.top
      if (blockHeight <= maxSlicePx) {
        sliceEnd = block.top
      }
      break
    }
  }
  return sliceEnd
}

function sanitizeFileName(value: string): string {
  return value.replace(/[/\\:*?"<>|]/g, '_')
}

function formatDate(iso?: string | null): string {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' })
  } catch {
    return iso
  }
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = reject
    img.src = src
  })
}

function headerHeight(logoImg: HTMLImageElement | null): number {
  const logoWidth = 90
  const logoHeight = logoImg ? (logoImg.height / logoImg.width) * logoWidth : 0
  const titleY = MARGIN_PT + 12
  const detailsY = titleY + 16
  const detailsBottom = detailsY + 2 * 12 // 3 detail rows, 12pt apart
  const dividerY = Math.max(MARGIN_PT + logoHeight, detailsBottom) + 6
  return (dividerY + 8) - MARGIN_PT
}

/**
 * Draws the report header, repeated identically on every page: the VVSI logo
 * fixed at the left, and the report title + claim details right-aligned as a
 * single block along the right margin, title directly above the details.
 */
function drawHeader(
  pdf: jsPDF,
  claim: ClaimGetResponse,
  vehicleLabel: string,
  logoImg: HTMLImageElement | null
): void {
  const pageWidth = PDF_WIDTH_PT
  const cursorY = MARGIN_PT

  const logoWidth = 90
  const logoHeight = logoImg ? (logoImg.height / logoImg.width) * logoWidth : 0
  if (logoImg) {
    pdf.addImage(logoImg, 'PNG', MARGIN_PT, cursorY, logoWidth, logoHeight)
  }

  const rightX = pageWidth - MARGIN_PT

  // Title, right-aligned along the page margin.
  pdf.setFont('helvetica', 'bold')
  pdf.setFontSize(14)
  pdf.setTextColor(20, 20, 20)
  const titleY = cursorY + 12
  pdf.text('Vehicle Valuation Inspection Report', rightX, titleY, { align: 'right' })

  // Label (bold) + value (normal) pairs, in this exact order, each line
  // right-aligned so it ends flush with the title above it along the margin.
  const detailRows = [
    { label: 'Vehicle Type: ', value: vehicleLabel || '—' },
    { label: 'Claim Reference No: ', value: claim.claimReferenceNumber || '—' },
    { label: 'Claim Received Date: ', value: formatDate(claim.dateClaimReceived) },
  ]
  const lineHeight = 12
  const detailsY = titleY + 16

  detailRows.forEach((row, i) => {
    const y = detailsY + i * lineHeight
    pdf.setFontSize(9)
    pdf.setFont('helvetica', 'normal')
    const valueWidth = pdf.getTextWidth(row.value)
    pdf.setTextColor(90, 90, 90)
    pdf.text(row.value, rightX, y, { align: 'right' })
    pdf.setFont('helvetica', 'bold')
    pdf.setTextColor(50, 50, 50)
    pdf.text(row.label, rightX - valueWidth, y, { align: 'right' })
  })
  const detailsBottomY = detailsY + (detailRows.length - 1) * lineHeight

  const dividerY = Math.max(cursorY + logoHeight, detailsBottomY) + 6
  pdf.setDrawColor(200, 200, 200)
  pdf.line(MARGIN_PT, dividerY, pageWidth - MARGIN_PT, dividerY)
}

/** Draws the page footer: page number on the left, report-generated date on the right. */
function drawFooter(pdf: jsPDF, pageNum: number, totalPages: number, generatedOn: string): void {
  const pageWidth = PDF_WIDTH_PT
  const footerY = PDF_HEIGHT_PT - MARGIN_PT + 12

  pdf.setDrawColor(200, 200, 200)
  pdf.line(MARGIN_PT, footerY - 10, pageWidth - MARGIN_PT, footerY - 10)

  pdf.setFont('helvetica', 'normal')
  pdf.setFontSize(8)
  pdf.setTextColor(120, 120, 120)
  pdf.text(`Page ${pageNum} of ${totalPages}`, MARGIN_PT, footerY)
  pdf.text(`Report Generated: ${generatedOn}`, pageWidth - MARGIN_PT, footerY, { align: 'right' })
}

/**
 * Renders the given claim using the same section layout as the View Claim page,
 * then paginates it into a PDF with a branded header repeated on every page.
 */
export async function exportClaimToPdf(
  claim: ClaimGetResponse,
  getLabelById: (category: string, id: number) => string | undefined
): Promise<void> {
  const vehicleLabel = getLabelById('vehicle_type', claim.vehicleTypeId) ?? String(claim.vehicleTypeId)

  const container = document.createElement('div')
  container.style.position = 'fixed'
  container.style.top = '0'
  container.style.left = '-9999px'
  container.style.width = `${RENDER_WIDTH_PX}px`
  container.style.background = '#ffffff'
  document.body.appendChild(container)

  const root = createRoot(container)

  try {
    await new Promise<void>((resolve) => {
      root.render(createElement(ClaimDetailView, { claim, getLabelById, forPdf: true }))
      requestAnimationFrame(() => requestAnimationFrame(() => resolve()))
    })

    const blocks = getBlocks(container)

    const [canvas, logoImg] = await Promise.all([
      html2canvas(container, { scale: CANVAS_SCALE, backgroundColor: '#ffffff', useCORS: true }),
      loadImage(vvsiLogo).catch(() => null),
    ])

    const pdf = new jsPDF({ unit: 'pt', format: 'a4' })

    const headerH = headerHeight(logoImg)
    const contentTop = MARGIN_PT + headerH
    const usableWidth = PDF_WIDTH_PT - MARGIN_PT * 2
    const usableHeightPerPage = PDF_HEIGHT_PT - contentTop - MARGIN_PT

    const pxToPt = usableWidth / canvas.width
    const maxSlicePx = usableHeightPerPage / pxToPt

    let renderedPx = 0
    let pageIndex = 0

    while (renderedPx < canvas.height) {
      const sliceEnd = computeSliceEnd(blocks, renderedPx, maxSlicePx, canvas.height)
      const sliceHeightPx = sliceEnd - renderedPx

      if (pageIndex > 0) pdf.addPage()
      drawHeader(pdf, claim, vehicleLabel, logoImg)

      const sliceCanvas = document.createElement('canvas')
      sliceCanvas.width = canvas.width
      sliceCanvas.height = sliceHeightPx
      const ctx = sliceCanvas.getContext('2d')
      if (ctx) {
        ctx.drawImage(
          canvas,
          0, renderedPx, canvas.width, sliceHeightPx,
          0, 0, canvas.width, sliceHeightPx
        )
      }
      const sliceData = sliceCanvas.toDataURL('image/png')
      const sliceHeightPt = sliceHeightPx * pxToPt
      pdf.addImage(sliceData, 'PNG', MARGIN_PT, contentTop, usableWidth, sliceHeightPt)

      renderedPx += sliceHeightPx
      pageIndex += 1
    }

    const totalPages = pdf.getNumberOfPages()
    const generatedOn = formatDate(new Date().toISOString())
    for (let i = 1; i <= totalPages; i++) {
      pdf.setPage(i)
      drawFooter(pdf, i, totalPages, generatedOn)
    }

    const fileName = `Vehicle_Inspection_${sanitizeFileName(claim.claimReferenceNumber || claim.caseId || 'claim')}.pdf`
    pdf.save(fileName)
  } catch (err) {
    throw err instanceof Error ? err : new Error(getMessage('dashboard.export_error'))
  } finally {
    root.unmount()
    container.remove()
  }
}
